import { useParams, useSearchParams } from "react-router-dom";
import { LessonShell } from "@/components/LessonShell";
import { useCompanionLesson } from "@/hooks/useCompanionData";
import { COURSE_LABEL } from "@/data/lessons";

/** "jazz-basics" → "Jazz Basics" for a course that isn't in the baked map. */
function prettySlug(slug: string): string {
  return slug
    .split("-")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/**
 * GenericLesson — a fully data-driven interactive lesson.
 *
 * Used by the catch-all route for any /lesson/<slug> that does NOT have a
 * hand-built TSX component (the 163 bespoke lessons keep their own explicit
 * routes and always win the match — this only handles brand-new slugs).
 *
 * Everything renders from the live CompanionContent the admin edits in the LMS
 * "Admin → Companion" editor: video/title/description here, and the prose /
 * notation / MIDI are rendered automatically by LessonShell via CompanionDynamic.
 * So a new interactive lesson needs ZERO code — just fill the editor.
 *
 * Safe by construction: if there's no content/video for the slug yet, it shows
 * a gentle "preparing" panel rather than crashing or showing a blank screen.
 */
export default function GenericLesson() {
  const { slug = "" } = useParams();
  // slug like "the-beatles-12" or "south-indian-5" -> courseSlug + trailing order
  const m = slug.match(/^(.+)-(\d+)$/);
  const courseSlug = m ? m[1] : slug;
  const order = m ? m[2] : "1";

  const data = useCompanionLesson(courseSlug, order);
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get("embed") === "1";
  // Course label: baked label if the course exists, else prettified slug.
  // (Never the lesson title — that would duplicate the H1 in the eyebrow.)
  const courseLabel =
    (COURSE_LABEL as Record<string, string>)[courseSlug] ?? prettySlug(courseSlug);

  if (!data || !data.videoUrl) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-4 px-6 text-center ${
          isEmbed ? "py-24" : "min-h-screen"
        }`}
      >
        <p className="text-lg font-semibold text-nsm-ink">Preparing this lesson…</p>
        <p className="text-sm text-nsm-ink/60">
          If this lesson was just added, give it a moment. Otherwise it may not be
          published yet.
        </p>
        <a
          href="https://learn-music.nathanielschool.com"
          className="text-sm font-semibold underline"
        >
          Back to your courses
        </a>
      </div>
    );
  }

  return (
    <LessonShell
      course={courseLabel}
      courseSlug={courseSlug}
      lessonOrder={order}
      title={data.title || ""}
      description={data.description || ""}
      videoUrl={data.videoUrl}
      thumbUrl={data.thumbUrl}
    >
      <></>
    </LessonShell>
  );
}
